import React, { useState, useEffect } from 'react';
import { Box, Container, Card, TextField, Button, CircularProgress, Typography, Alert, Snackbar, Grid, List, ListItem, ListItemText, IconButton } from '@mui/material';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { campaignService, campaignCategoryService, extractApiDataList } from '../services/api';
import { ArrowBack, CloudUpload, Save, Delete, PictureAsPdf, InsertDriveFile } from '@mui/icons-material';
import { getAssetUrl } from '../config';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

/** Normalize list responses like `{ success, data: [] }`, `{ data: [] }`, or a bare array. */
function extractListFromApiResponse(res) {
  if (!res || res.data === undefined || res.data === null) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d.data)) return d.data;
  return [];
}

/** Later lists override earlier rows with the same `id` (endpoint wins over nested campaign payload). */
function mergeRowsById(...lists) {
  const map = new Map();
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const row of list) {
      if (!row || row.id == null) continue;
      map.set(String(row.id), row);
    }
  }
  return Array.from(map.values()).sort((a, b) => {
    const ao = Number(a.display_order) || 0;
    const bo = Number(b.display_order) || 0;
    if (ao !== bo) return ao - bo;
    return Number(a.id) - Number(b.id);
  });
}

const showCampaignProductsEditor = (variant) => variant === 'school_kit' || variant === 'freedom_hunger';

const CampaignEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [documents, setDocuments] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    content: '',
    target_amount: '',
    current_amount: '0',
    start_date: '',
    end_date: '',
    status: 'active',
    donation_variant: 'default',
    category_id: '',
    image_url: '',
    video_url: '',
    map_embed_url: '',
    map_lat: '',
    map_lng: '',
    map_address: ''
  });
  const [products, setProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({ product_name: '', description: '', price: '', quantity: '' });
  const [essentials, setEssentials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newEssential, setNewEssential] = useState({
    item_name: '',
    description: '',
    unit_price: '',
    display_order: '',
    is_active: '1',
  });

  useEffect(() => {
    fetchCampaign();
  }, [id]);

  const fetchCampaign = async () => {
    try {
      setLoading(true);
      const [campaignRes, productsRes, essentialsRes, categoriesRes] = await Promise.all([
        campaignService.getCampaignById(id),
        campaignService.getCampaignProducts(id).catch(() => ({ data: { success: true, data: [] } })),
        campaignService.getCampaignEssentials(id).catch(() => ({ data: { success: true, data: [] } })),
        campaignCategoryService.getAll({ include_inactive: 1 }).catch(() => ({ data: { success: true, data: [] } })),
      ]);
      const campaign = campaignRes.data?.data || campaignRes.data;
      const fromCampaignProducts = Array.isArray(campaign?.products) ? campaign.products : [];
      const fromEndpointProducts = extractListFromApiResponse(productsRes);
      const productsList = mergeRowsById(fromCampaignProducts, fromEndpointProducts);

      const fromCampaignEssentials = Array.isArray(campaign?.essentials) ? campaign.essentials : [];
      const fromEndpointEssentials = extractListFromApiResponse(essentialsRes);
      const essentialsList = mergeRowsById(fromCampaignEssentials, fromEndpointEssentials);

      const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toISOString().split('T')[0];
      };

      setCategories(extractApiDataList(categoriesRes));

      setFormData({
        title: campaign.title || '',
        slug: campaign.slug || '',
        description: campaign.description || '',
        content: campaign.content || '',
        target_amount: campaign.target_amount || '',
        current_amount: campaign.current_amount || '0',
        start_date: formatDate(campaign.start_date),
        end_date: formatDate(campaign.end_date),
        status: campaign.status || 'active',
        donation_variant: campaign.donation_variant || 'default',
        category_id: campaign.category_id ?? campaign.category?.id ?? '',
        image_url: campaign.image_url || '',
        video_url: campaign.video_url || '',
        map_embed_url: campaign.map_embed_url || '',
        map_lat: campaign.map_lat ?? '',
        map_lng: campaign.map_lng ?? '',
        map_address: campaign.map_address || ''
      });
      setDocuments(campaign.documents || []);
      setProducts(productsList);
      setEssentials(essentialsList);
      if (campaign.image_url) {
        setImagePreview(campaign.image_url);
      }
    } catch (error) {
      console.error('Error fetching campaign:', error);
      setToast({
        open: true,
        message: 'Error loading campaign',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name);
      const res = await campaignService.uploadCampaignDocument(id, fd);
      setDocuments(prev => [...prev, res.data?.data || res.data]);
      setToast({ open: true, message: 'Document uploaded', type: 'success' });
    } catch (err) {
      setToast({ open: true, message: err.response?.data?.error || 'Upload failed', type: 'error' });
    }
    e.target.value = '';
  };

  const handleProductFieldChange = (productId, field, value) => {
    setProducts((prev) => prev.map((p) => (p.id === productId ? { ...p, [field]: value } : p)));
  };

  const handleSaveProduct = async (product) => {
    try {
      await campaignService.updateCampaignProduct(id, product.id, {
        product_name: product.product_name,
        description: product.description,
        price: parseFloat(product.price),
        quantity: product.quantity === '' || product.quantity == null ? null : parseInt(product.quantity, 10),
      });
      setToast({ open: true, message: 'Product updated', type: 'success' });
    } catch (err) {
      setToast({ open: true, message: err.response?.data?.error || 'Update failed', type: 'error' });
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Delete this sponsor tier / product?')) return;
    try {
      await campaignService.deleteCampaignProduct(id, productId);
      setProducts((prev) => prev.filter((p) => p.id !== productId));
      setToast({ open: true, message: 'Product deleted', type: 'success' });
    } catch (err) {
      setToast({ open: true, message: err.response?.data?.error || 'Delete failed', type: 'error' });
    }
  };

  const handleAddProduct = async () => {
    if (!newProduct.product_name || newProduct.price === '') {
      setToast({ open: true, message: 'Product name and price are required', type: 'error' });
      return;
    }
    try {
      const res = await campaignService.createCampaignProduct(id, {
        product_name: newProduct.product_name,
        description: newProduct.description || null,
        price: parseFloat(newProduct.price),
        quantity: newProduct.quantity === '' ? null : parseInt(newProduct.quantity, 10),
      });
      const row = res.data?.data || res.data;
      if (row) setProducts((prev) => [...prev, row]);
      setNewProduct({ product_name: '', description: '', price: '', quantity: '' });
      setToast({ open: true, message: 'Product added', type: 'success' });
    } catch (err) {
      setToast({ open: true, message: err.response?.data?.error || 'Add failed', type: 'error' });
    }
  };

  const refetchProducts = async () => {
    try {
      const res = await campaignService.getCampaignProducts(id);
      const list = res.data?.data ?? res.data ?? [];
      setProducts(Array.isArray(list) ? list : []);
    } catch (_) {
      /* ignore */
    }
  };

  const refetchEssentials = async () => {
    try {
      const res = await campaignService.getCampaignEssentials(id);
      const list = res.data?.data ?? res.data ?? [];
      setEssentials(Array.isArray(list) ? list : []);
    } catch (_) {
      /* ignore */
    }
  };

  const handleEssentialFieldChange = (essentialId, field, value) => {
    setEssentials((prev) => prev.map((e) => (e.id === essentialId ? { ...e, [field]: value } : e)));
  };

  const handleSaveEssential = async (row) => {
    try {
      await campaignService.updateCampaignEssential(id, row.id, {
        item_name: row.item_name,
        description: row.description || null,
        unit_price: parseFloat(row.unit_price),
        display_order: row.display_order === '' || row.display_order == null ? 0 : parseInt(row.display_order, 10),
        is_active: row.is_active === 1 || row.is_active === true || row.is_active === '1',
      });
      setToast({ open: true, message: 'Essential updated', type: 'success' });
      await refetchEssentials();
    } catch (err) {
      setToast({ open: true, message: err.response?.data?.error || 'Update failed', type: 'error' });
    }
  };

  const handleDeleteEssential = async (essentialId) => {
    if (!window.confirm('Delete this Donate Essentials line?')) return;
    try {
      await campaignService.deleteCampaignEssential(id, essentialId);
      setEssentials((prev) => prev.filter((e) => e.id !== essentialId));
      setToast({ open: true, message: 'Deleted', type: 'success' });
    } catch (err) {
      setToast({ open: true, message: err.response?.data?.error || 'Delete failed', type: 'error' });
    }
  };

  const handleAddEssential = async () => {
    if (!newEssential.item_name || newEssential.unit_price === '') {
      setToast({ open: true, message: 'Item name and unit price are required', type: 'error' });
      return;
    }
    try {
      const res = await campaignService.createCampaignEssential(id, {
        item_name: newEssential.item_name,
        description: newEssential.description || null,
        unit_price: parseFloat(newEssential.unit_price),
        display_order: newEssential.display_order === '' ? 0 : parseInt(newEssential.display_order, 10),
        is_active: newEssential.is_active === '1' || newEssential.is_active === 1,
      });
      const row = res.data?.data || res.data;
      if (row) setEssentials((prev) => [...prev, row]);
      setNewEssential({ item_name: '', description: '', unit_price: '', display_order: '', is_active: '1' });
      setToast({ open: true, message: 'Essential added', type: 'success' });
    } catch (err) {
      setToast({ open: true, message: err.response?.data?.error || 'Add failed', type: 'error' });
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await campaignService.deleteCampaignDocument(id, docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      setToast({ open: true, message: 'Document deleted', type: 'success' });
    } catch (err) {
      setToast({ open: true, message: err.response?.data?.error || 'Delete failed', type: 'error' });
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.title || !formData.slug || !formData.description) {
        setToast({
          open: true,
          message: 'Please fill in all required fields (Title, Slug, Description)',
          type: 'error'
        });
        return;
      }

      setSaving(true);

      if (selectedImage) {
        const formDataToSend = new FormData();
        formDataToSend.append('image', selectedImage);
        formDataToSend.append('title', formData.title);
        formDataToSend.append('slug', formData.slug);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('content', formData.content);
        formDataToSend.append('target_amount', formData.target_amount);
        formDataToSend.append('current_amount', formData.current_amount);
        formDataToSend.append('start_date', formData.start_date);
        formDataToSend.append('end_date', formData.end_date);
        formDataToSend.append('status', formData.status);
        formDataToSend.append('video_url', formData.video_url);
        formDataToSend.append('map_embed_url', formData.map_embed_url || '');
        formDataToSend.append('map_lat', formData.map_lat || '');
        formDataToSend.append('map_lng', formData.map_lng || '');
        formDataToSend.append('map_address', formData.map_address || '');
        formDataToSend.append('donation_variant', formData.donation_variant || 'default');
        formDataToSend.append('category_id', formData.category_id === '' ? '' : String(formData.category_id));

        await campaignService.updateCampaignWithFile(id, formDataToSend);
      } else {
        await campaignService.updateCampaign(id, formData);
      }

      setToast({
        open: true,
        message: 'Campaign updated successfully!',
        type: 'success'
      });

      await refetchProducts();
      await refetchEssentials();

      setTimeout(() => {
        navigate('/admin/campaigns');
      }, 1500);
    } catch (error) {
      console.error('Error updating campaign:', error);
      setToast({
        open: true,
        message: 'Error updating campaign: ' + (error.response?.data?.error || error.message),
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', py: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/campaigns')}
            sx={{ color: '#2c5f2d' }}
          >
            Back to Campaigns
          </Button>
        </Box>

        <Card sx={{ 
          border: '1px solid #e8ecf1',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          p: 3
        }}>
          <Typography variant="h5" sx={{ fontWeight: '600', color: '#1a202c', mb: 3 }}>
            Edit Campaign: {formData.title}
          </Typography>

          <Grid container spacing={3}>
            {/* Image Upload Section */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#f8fafb', borderRadius: '8px', border: '1px solid #e8ecf1' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 2 }}>
                  Campaign Image
                </Typography>
                
                {imagePreview && (
                  <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <img 
                      src={getAssetUrl(imagePreview)} 
                      alt="Campaign preview" 
                      style={{
                        maxWidth: '100%',
                        maxHeight: '250px',
                        borderRadius: '8px',
                        border: '2px solid #e8ecf1'
                      }}
                    />
                  </Box>
                )}

                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUpload />}
                  sx={{
                    borderColor: '#2c5f2d',
                    color: '#2c5f2d',
                    py: 2,
                    '&:hover': {
                      backgroundColor: '#f0f7ff',
                      borderColor: '#1f4620',
                    }
                  }}
                >
                  {selectedImage ? 'Change Image' : 'Upload New Image'}
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleImageUpload}
                  />
                </Button>
                <Typography variant="caption" sx={{ color: '#667085', mt: 1, display: 'block' }}>
                  Recommended: 400x250 pixels (JPG or PNG, max 5MB)
                </Typography>
              </Box>
            </Grid>

            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 2 }}>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Campaign Title *"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Slug *"
                    name="slug"
                    value={formData.slug}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description *"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Detailed Content"
                    name="content"
                    value={formData.content}
                    onChange={handleFormChange}
                    multiline
                    rows={4}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Financial Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 2 }}>
                Financial Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Target Amount"
                    name="target_amount"
                    type="number"
                    value={formData.target_amount}
                    onChange={handleFormChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Current Amount"
                    name="current_amount"
                    type="number"
                    value={formData.current_amount}
                    onChange={handleFormChange}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Timeline */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 2 }}>
                Campaign Timeline
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    name="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={handleFormChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    name="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={handleFormChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Additional Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 2 }}>
                Additional Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    name="status"
                    value={formData.status}
                    onChange={handleFormChange}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="completed">Completed</option>
                    <option value="draft">Draft</option>
                    <option value="deleted">Deleted</option>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Category"
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleFormChange}
                    SelectProps={{ native: true }}
                    helperText={
                      <span>
                        Manage categories in{' '}
                        <Link to="/admin/campaign-categories" style={{ color: '#2c5f2d' }}>
                          Campaign Categories
                        </Link>
                      </span>
                    }
                  >
                    <option value="">— None —</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}{cat.is_active ? '' : ' (inactive)'}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Donation form type"
                    name="donation_variant"
                    value={formData.donation_variant}
                    onChange={handleFormChange}
                    SelectProps={{ native: true }}
                    helperText="School kit: sponsor tiers + extras. Freedom from hunger: preset amounts + DOB."
                  >
                    <option value="default">Standard (default)</option>
                    <option value="school_kit">School kit distribution</option>
                    <option value="freedom_hunger">Freedom from hunger</option>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Video URL"
                    name="video_url"
                    value={formData.video_url}
                    onChange={handleFormChange}
                    placeholder="https://youtube.com/..."
                  />
                </Grid>
              </Grid>
            </Grid>

            {showCampaignProductsEditor(formData.donation_variant) && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 2 }}>
                  Sponsor tiers (products)
                </Typography>
                <Typography variant="body2" sx={{ color: '#667085', mb: 2 }}>
                  {formData.donation_variant === 'school_kit'
                    ? 'Each row is one tier on the public page (price = suggested monthly support, quantity = number of children).'
                    : 'Each row is a suggested donation amount on the public page (quantity optional).'}
                </Typography>
                {products.length === 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No products are stored for this campaign yet. Use &quot;Add tier&quot; below, or insert rows into{' '}
                    <code>ngo_campaign_products</code> for this campaign&apos;s ID ({id}).
                  </Alert>
                )}
                {products.map((p) => (
                  <Grid container spacing={1} key={p.id} sx={{ mb: 2, alignItems: 'center' }}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Name"
                        value={p.product_name}
                        onChange={(e) => handleProductFieldChange(p.id, 'product_name', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Description"
                        value={p.description || ''}
                        onChange={(e) => handleProductFieldChange(p.id, 'description', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Price (₹)"
                        type="number"
                        value={p.price}
                        onChange={(e) => handleProductFieldChange(p.id, 'price', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label={formData.donation_variant === 'school_kit' ? 'Children #' : 'Qty (opt.)'}
                        type="number"
                        value={p.quantity ?? ''}
                        onChange={(e) => handleProductFieldChange(p.id, 'quantity', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={2} sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant="contained" onClick={() => handleSaveProduct(p)} sx={{ backgroundColor: '#2c5f2d' }}>
                        Save
                      </Button>
                      <Button size="small" color="error" variant="outlined" onClick={() => handleDeleteProduct(p.id)}>
                        Delete
                      </Button>
                    </Grid>
                  </Grid>
                ))}
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Add tier</Typography>
                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label="Name" value={newProduct.product_name} onChange={(e) => setNewProduct((s) => ({ ...s, product_name: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label="Description" value={newProduct.description} onChange={(e) => setNewProduct((s) => ({ ...s, description: e.target.value }))} />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField fullWidth size="small" label="Price" type="number" value={newProduct.price} onChange={(e) => setNewProduct((s) => ({ ...s, price: e.target.value }))} />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField fullWidth size="small" label={formData.donation_variant === 'school_kit' ? 'Children #' : 'Qty (opt.)'} type="number" value={newProduct.quantity} onChange={(e) => setNewProduct((s) => ({ ...s, quantity: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button variant="outlined" onClick={handleAddProduct} sx={{ borderColor: '#2c5f2d', color: '#2c5f2d' }}>Add</Button>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {(formData.donation_variant === 'school_kit' || formData.donation_variant === 'freedom_hunger') && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 2 }}>
                  Donate Essentials (public page)
                </Typography>
                <Typography variant="body2" sx={{ color: '#667085', mb: 2 }}>
                  {formData.donation_variant === 'school_kit'
                    ? 'Each row appears on the School Kit campaign with − / + controls; unit price is added to the donor total.'
                    : 'Each row appears on the Freedom From Hunger campaign with − / + controls; unit price is added to the donor total.'}
                </Typography>
                {essentials.length === 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No essentials yet. Add rows below, or seed <code>ngo_campaign_essentials</code> for this campaign.
                  </Alert>
                )}
                {essentials.map((e) => (
                  <Grid container spacing={1} key={e.id} sx={{ mb: 2, alignItems: 'center' }}>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Item name"
                        value={e.item_name}
                        onChange={(ev) => handleEssentialFieldChange(e.id, 'item_name', ev.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Description"
                        value={e.description || ''}
                        onChange={(ev) => handleEssentialFieldChange(e.id, 'description', ev.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="₹ / unit"
                        type="number"
                        value={e.unit_price}
                        onChange={(ev) => handleEssentialFieldChange(e.id, 'unit_price', ev.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} md={1}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Order"
                        type="number"
                        value={e.display_order ?? ''}
                        onChange={(ev) => handleEssentialFieldChange(e.id, 'display_order', ev.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} md={1}>
                      <TextField
                        fullWidth
                        size="small"
                        select
                        label="Active"
                        value={e.is_active === 0 || e.is_active === false ? '0' : '1'}
                        onChange={(ev) => handleEssentialFieldChange(e.id, 'is_active', ev.target.value === '1' ? 1 : 0)}
                        SelectProps={{ native: true }}
                      >
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={2} sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant="contained" onClick={() => handleSaveEssential(e)} sx={{ backgroundColor: '#2c5f2d' }}>
                        Save
                      </Button>
                      <Button size="small" color="error" variant="outlined" onClick={() => handleDeleteEssential(e.id)}>
                        Delete
                      </Button>
                    </Grid>
                  </Grid>
                ))}
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                  Add essential line
                </Typography>
                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Item name"
                      value={newEssential.item_name}
                      onChange={(ev) => setNewEssential((s) => ({ ...s, item_name: ev.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Description"
                      value={newEssential.description}
                      onChange={(ev) => setNewEssential((s) => ({ ...s, description: ev.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="₹ / unit"
                      type="number"
                      value={newEssential.unit_price}
                      onChange={(ev) => setNewEssential((s) => ({ ...s, unit_price: ev.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Order"
                      type="number"
                      value={newEssential.display_order}
                      onChange={(ev) => setNewEssential((s) => ({ ...s, display_order: ev.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Active"
                      value={newEssential.is_active}
                      onChange={(ev) => setNewEssential((s) => ({ ...s, is_active: ev.target.value }))}
                      SelectProps={{ native: true }}
                    >
                      <option value="1">Yes</option>
                      <option value="0">No</option>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button variant="outlined" onClick={handleAddEssential} sx={{ borderColor: '#2c5f2d', color: '#2c5f2d' }}>
                      Add
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* Map */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 2 }}>
                Map
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Map embed URL (iframe src)"
                    name="map_embed_url"
                    value={formData.map_embed_url}
                    onChange={handleFormChange}
                    placeholder="https://www.google.com/maps/embed?pb=..."
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Latitude" name="map_lat" value={formData.map_lat} onChange={handleFormChange} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Longitude" name="map_lng" value={formData.map_lng} onChange={handleFormChange} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Address / label" name="map_address" value={formData.map_address} onChange={handleFormChange} />
                </Grid>
              </Grid>
            </Grid>

            {/* Documents */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 2 }}>
                Documents
              </Typography>
              <List dense sx={{ bgcolor: '#f8fafb', borderRadius: 1, mb: 2 }}>
                {documents.length === 0 && (
                  <ListItem><ListItemText primary="No documents" secondary="Upload PDF or other files" /></ListItem>
                )}
                {documents.map((doc) => (
                  <ListItem
                    key={doc.id}
                    secondaryAction={
                      <IconButton edge="end" onClick={() => handleDeleteDocument(doc.id)} size="small" aria-label="Delete">
                        <Delete />
                      </IconButton>
                    }
                  >
                    {doc.file_type?.includes('pdf') ? <PictureAsPdf sx={{ mr: 1, color: '#c62828' }} /> : <InsertDriveFile sx={{ mr: 1 }} />}
                    <ListItemText
                      primary={doc.title}
                      secondary={doc.file_url}
                    />
                    <Button size="small" href={getAssetUrl(doc.file_url)} target="_blank" rel="noopener noreferrer">Open</Button>
                  </ListItem>
                ))}
              </List>
              <Button variant="outlined" component="label" startIcon={<CloudUpload />} sx={{ borderColor: '#2c5f2d', color: '#2c5f2d' }}>
                Upload document
                <input hidden accept=".pdf,.doc,.docx,image/*,.txt" type="file" onChange={handleUploadDocument} />
              </Button>
            </Grid>

            {/* Actions */}
            <Grid item xs={12} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/admin/campaigns')}
                sx={{ borderColor: '#e8ecf1', color: '#667085' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={saving}
                sx={{ 
                  backgroundColor: '#2c5f2d',
                  '&:hover': { backgroundColor: '#1f4620' }
                }}
              >
                {saving ? <CircularProgress size={24} /> : 'Update Campaign'}
              </Button>
            </Grid>
          </Grid>
        </Card>
      </Container>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.type}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CampaignEditPage;
